/**
 * @file src/voice/Deck.js
 * @description The per-player DECK — the ONE owner of everything "what does this player play, and to
 * whose clock": sync-enable, warp, transport tempo, native track tempo, meter, grid marker, follow
 * ratio, and the beat-clock seam. It folds the former SyncView (per-voice notification fan),
 * WrapGridState (grid/meter/ratio state), voiceSyncEnable and voiceWrapEnable (registry-record
 * flags) into one module, so the "is THIS voice synced → which clock → which tempo" rule exists at
 * exactly one site instead of being hand-assembled per feature.
 *
 * The tempo model (the DJ-deck model, per Bruna):
 *  - Every deck has ONE continuous **transport tempo** (`tempo`, the header "BPM" number). While
 *    synced it tracks the shared master; while unsynced it is the deck's own. Toggling sync only
 *    changes WHO DRIVES the number, never the number itself at the seam — un-syncing HOLDS the last
 *    session tempo (no revert, no audible jump); syncing on as the first enabler ESTABLISHES the
 *    master from this deck's tempo; syncing on later ADOPTS the running master.
 *  - **Warp** means "time-stretch the audio to the transport tempo" and is independent of sync: a
 *    synced deck warps to the shared tempo, an unsynced deck warps to its OWN tempo (a varispeed /
 *    pitch-fader), warp OFF plays natural (no grid). Solo (non-collection) keeps the historical
 *    rule — it follows only while the sync session is enabled — so single-orbiter playback is
 *    byte-identical.
 *  - The transport tempo is session-only state. What persists per track is the NATIVE tempo
 *    (`nativeTempo`, the analyzed/edited track BPM — the grid reference), via the kit panel's
 *    commit seam. Persisting the transport tempo is exactly how sibling tempos used to poison a
 *    track's saved settings.
 *
 * Notification: the deck emits ONE deck-scoped change stream, `onChange(snapshot, reason)`
 * ("deck-changed"). The coordinator's session channel (`syncCoordinator.onBpmChange/onStatusChange`)
 * is the master/session stream ("master-changed") — audience is named by the owner you subscribe to,
 * never by which of two pipes a caller happened to pick.
 */
import { voiceRegistry } from './VoiceRegistry.js';
import { syncCoordinator } from '../sync/SyncCoordinator.js';
import { wrapSourcePositionMs } from '../sync/wrap/WrapGridMap.js';
import {
  resolvePrimaryGridMarkerTimeSecFromTrackData,
  resolveTrackBpmFromTrackData,
  resolveWarpEnabledFromTrackData,
} from 'entangled-worlds-orbiters-shared/clock/track-metadata';
import { resolveTrackMeterFromTrackData, normalizeMeterId, sharedBeatsPerBar } from '../sync/meter.js';
import { getLaunchGridBars } from '../sync/launchGrid.js';
import { clampTempo } from '../sync/tempoRange.js';

export const WRAP_GRID_CHANGE_EVENT = 'orbiters:wrap-grid-change';

function clampGridStartTimeSec(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, numeric);
}

function buildGridMarker(sourceTimeSec = 0) {
  const resolvedSourceTimeSec = clampGridStartTimeSec(sourceTimeSec);
  return Object.freeze({
    id: 'grid-marker-1',
    beat: 1,
    kind: 'downbeat',
    label: 'Grid marker',
    sourceTimeSec: resolvedSourceTimeSec,
  });
}

function toPositiveNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Resolve a voice's deck: the given id, or the active/only voice (single-orbiter passes null —
 *  `useEngineVoiceId` is null there — and the one registered voice is the active one).
 *  @param {string|null} [voiceId]
 *  @returns {Deck|null} */
export function deckFor(voiceId = null) {
  const record = voiceRegistry.get(voiceId) ?? voiceRegistry.get(voiceRegistry.activeId);
  return record?.deck ?? null;
}

/** How many decks in THIS realm currently want sync (the in-tab "session" size — the SYNC badge
 *  counts these in-tab partners, not just cross-tab BroadcastChannel peers). */
export function syncEnabledDeckCount() {
  return voiceRegistry.all().filter((v) => v?.deck?.syncEnabled === true).length;
}

/** Drive the shared coordinator from the realm aggregate (its existing idempotent enable/disable),
 *  then re-fan per-voice status so each deck hears its OWN new enable state (enable/disable emit
 *  nothing when the aggregate is unchanged). */
export function recomputeSyncAggregate() {
  const anyOn = voiceRegistry.all().some((v) => v?.deck?.syncEnabled === true);
  if (anyOn) syncCoordinator.enable();
  else syncCoordinator.disable();
  syncCoordinator.notifyVoiceSyncChanged?.();
}

export class Deck {
  #voiceId = null;
  #collection = false;
  #syncEnabled = false;
  /** Has the sync-on default been applied to this deck yet? (One shot — see `#applyDefaultSync`.) */
  #defaultSyncApplied = false;
  #warp = true;
  /** This track's SAVED warp preference, as the server resolved it: this user's own choice, else the
   *  owner's, else the smart default from tempo and track type. Null = nobody has an opinion, so the
   *  historical default (on) stands. Held separately from `#warp` because `#warp` is the live value —
   *  it moves with speed locks, no-tempo locks and room events, none of which are preferences. */
  #warpPreference = null;
  // Mobile speed lock: while set, this deck's audio cannot time-stretch (the engine pins it to native
  // rate). Warp's only job is that stretch, so a locked deck is forced warp-off and can't re-enable it.
  #speedLocked = false;
  // Room WARP bridge (room mode only, installed by the room client). When present, a USER warp toggle
  // is a SHARED event: `canControl` gates whose input may drive this deck's warp, and an allowed
  // toggle is applied AND published. A remote apply passes `{ roomRemote: true }` so it neither gates
  // nor re-publishes. Internal forced flips (speed lock, no-tempo restore) bypass `setWarp` entirely,
  // so device-local constraints never leak onto the wire.
  #roomWarp = null;
  // Room SYNC bridge (room mode only, installed by the room client). Same contract as `#roomWarp`:
  // a USER sync toggle is a SHARED event — gated by `canControl`, applied locally, and published.
  #roomSync = null;
  // Room GRID bridge (room mode only, installed by the room client) for the three deck-owned values
  // every client must agree on to compute the same playhead: the native (audiofile) tempo and the
  // downbeat are the projection's divisor and origin, and the launch grid names the boundary a
  // shared start/seek/correction quantizes to. One bridge with one method per value, the same shape
  // the engine's loop bridge uses — the alternative is three near-identical bridges for one concern.
  // A remote apply passes `{ roomRemote: true }` so it neither gates nor echoes; internal writes
  // (the room's own launch-grid override, the boot seed) pass it too, so nothing the user did not
  // choose reaches the wire.
  #roomGrid = null;
  // The transport tempo is only a construction-time placeholder (master default, no native known
  // yet) until the track's tempo arrives or someone deliberately sets it. See the ctor note.
  #tempoIsPlaceholder = false;
  /** The continuous transport tempo (the "BPM" number). Master-driven while synced. */
  #tempo = null;
  /** The track's native/analyzed BPM — the grid reference; the value that persists per track. */
  #nativeTempo = null;
  #meter;
  #gridMarkerTimeSec = 0;
  /** This deck's OWN launch grid, in bars (0 = none). Seeded from the realm boot value
   *  (`launchGrid.js` — the URL-pinnable default); per-deck from then on, like meter. */
  #launchGridBars = getLaunchGridBars();
  #sharedClockSource = null;
  #positionSource = null;
  /** The PHASE ANCHOR — "at shared beat B this voice was at raw source position S". Null means the
   *  grid-marker default (beat 0 of the shared clock at the grid marker). See `setSeekAnchor`. */
  #anchorBeat = null;
  #anchorSourceMs = null;
  /** Bumped whenever anything the beat→playhead projection reads moves (anchor, native tempo, grid
   *  marker, track). A correction scheduled against one projection must not fire against another. */
  #projectionGeneration = 0;
  #listeners = new Set();
  #lastStatusDetail = null;
  #disposed = false;

  /**
   * @param {{
   *   voiceId?: string|null,
   *   trackData?: object|null,
   *   gridMarkerTimeSec?: number|null,
   *   collection?: boolean,
   * }} [options] `collection` marks a multi-realm deck (chosen by the CONSTRUCTION PATH, never by
   * the live registry size — sibling voices register asynchronously, so size at construction lies).
   * A collection deck seeds its transport tempo from its OWN track (ratio 1 — nothing audibly
   * changes at load) and follows its own tempo while unsynced. A solo deck mirrors the master and
   * follows only while the sync session is enabled (historical single-orbiter behavior).
   */
  constructor({ voiceId = null, trackData = null, gridMarkerTimeSec = null, collection = false } = {}) {
    this.#voiceId = voiceId ?? null;
    this.#collection = collection === true;
    this.#gridMarkerTimeSec = gridMarkerTimeSec == null
      ? (resolvePrimaryGridMarkerTimeSecFromTrackData(trackData) ?? 0)
      : clampGridStartTimeSec(gridMarkerTimeSec);
    // Native is strictly per-voice for a collection deck — borrowing the shared singleton here
    // would adopt a SIBLING's tempo (the contamination class) and mask a genuinely tempo-less
    // track. A solo deck keeps the singleton fallback: there it describes its own track.
    this.#nativeTempo = resolveTrackBpmFromTrackData(trackData)
      ?? (this.#collection
        ? null
        : toPositiveNumber(syncCoordinator?.trackBpm ?? syncCoordinator?.detectedTrackBpm));
    // Transport tempo seed: a collection deck starts on ITS OWN track's tempo (so the header number
    // is honest from first paint and the warp ratio is exactly 1); a solo deck mirrors the master.
    this.#tempo = this.#collection
      ? (this.#nativeTempo ?? toPositiveNumber(syncCoordinator?.bpm))
      : toPositiveNumber(syncCoordinator?.bpm);
    // Decks are usually constructed BEFORE their trackData exists (two-phase seed), so a collection
    // deck seeded without a native tempo holds only a PLACEHOLDER (the master default). Remember
    // that: when the track's real tempo arrives it must be adopted — a placeholder is not "a tempo
    // that's already playing". Cleared by any deliberate write (user edit, synced adoption).
    this.#tempoIsPlaceholder = this.#collection && this.#nativeTempo == null;
    // This deck's OWN meter (a property of its track), resolved from ITS OWN trackData only — never
    // the shared singleton, so loading voice B never inherits voice A's meter. Meter is ALWAYS
    // per-deck: sync aligns clock/tempo/phase only (a 6/8 track can sync to a 4/4 one).
    this.#meter = resolveTrackMeterFromTrackData(trackData);
    // The track's saved warp preference, if the payload already carries one at construction time.
    this.#warpPreference = resolveWarpEnabledFromTrackData(trackData);
    this.#warp = this.#warpDefault();
    // No tempo → no grid reference: warp has nothing to stretch to, so it starts OFF and stays
    // locked until a tempo is set. Never a silent ratio-1 "stretch" that looks like a broken warp.
    // This outranks any saved preference — a stored "warp on" cannot conjure a grid that isn't there.
    if (this.#nativeTempo == null) this.#warp = false;
  }

  /** Warp as the saved preference asks, or the historical default (on) when nobody has said. Callers
   *  still apply the no-tempo and speed-lock rules on top; those are locks, not preferences. */
  #warpDefault() {
    return typeof this.#warpPreference === 'boolean' ? this.#warpPreference : true;
  }

  get voiceId() { return this.#voiceId; }
  get isCollection() { return this.#collection; }

  /** Two-phase seed: the deck is constructed at voice registration (so its flags are toggleable
   *  before audio exists), and the audio adapter seeds it from the loaded trackData here — native
   *  tempo, meter, grid marker, and (collection, still riding native) the transport tempo. */
  seedFromTrackData(trackData) {
    // A new track means a new grid: the phase anchor described the OLD source timeline and would
    // project nonsense onto this one. This is the only place it is dropped. The generation bump is
    // unconditional because the seed writes native tempo and grid marker DIRECTLY (not through the
    // setters that bump), so the projection moves here even when no anchor was set.
    this.clearSeekAnchor();
    this.#projectionGeneration += 1;
    const wasRidingNative = this.#tempo == null
      || this.#tempo === this.#nativeTempo
      || this.#tempoIsPlaceholder;
    const hadTempo = this.#nativeTempo != null;
    const native = resolveTrackBpmFromTrackData(trackData);
    if (native != null) this.#nativeTempo = native;
    // THE RESTORE. This is the load — the moment the user's saved warp choice for this
    // track has to come back. Resolve it before the tempo-restore below, so that path defaults to
    // the preference instead of a blanket "on", and apply it unconditionally: a deck is reused
    // across tracks, so the previous track's live warp is not this track's answer.
    this.#warpPreference = resolveWarpEnabledFromTrackData(trackData);
    this.#applySavedWarp();
    if (!hadTempo && this.#nativeTempo != null) this.#restoreWarpAfterTempo();
    this.#meter = resolveTrackMeterFromTrackData(trackData);
    const gridSec = resolvePrimaryGridMarkerTimeSecFromTrackData(trackData);
    if (gridSec != null) this.#gridMarkerTimeSec = clampGridStartTimeSec(gridSec);
    if (this.#collection && !this.#syncEnabled && wasRidingNative && this.#nativeTempo != null) {
      this.#tempo = this.#nativeTempo;
      this.#tempoIsPlaceholder = false;
    } else if (!this.#collection && this.#nativeTempo != null && syncCoordinator?.isEnabled !== true) {
      // LOAD — same rule for a SOLO deck: its transport tempo IS the session master (it mirrors it),
      // so adopting the track's tempo means seeding the master. Guarded there by "has anyone
      // deliberately chosen a tempo yet?" — a BPM the user typed while loading is respected, a boot
      // seed is not. The fan returns the value here through `receiveMasterChange`, so there is no
      // second tempo owner.
      syncCoordinator.seedTrackTempo?.(this.#nativeTempo);
    }
    this.#emit('bpm');
    this.applyDefaultSync();
  }

  // ── sync enable ──────────────────────────────────────────────────────────

  get syncEnabled() { return this.#syncEnabled; }

  /** Sync is ON by default — applied exactly once per deck, and only when it is SAFE to apply.
   *
   *  Two preconditions, and neither consumes the one shot when it isn't met:
   *
   *  - The coordinator must be initialized. Enabling drives it through `recomputeSyncAggregate`, and
   *    it has no adapter to connect until `initSync` runs — which happens AFTER the audio engine (and
   *    so after the seed below) on the primary voice. Boot therefore sweeps the registered decks from
   *    `initSync`; a voice built later is already past that and applies on its own seed.
   *  - The deck must have a native tempo. The first deck to enable ESTABLISHES the session master
   *    from its tempo, and the tempo adoption in `seedFromTrackData` is gated on sync being OFF — so
   *    enabling on a tempo-less seed would fix the master at the placeholder BPM, and the real tempo
   *    arriving later would then play at placeholder/native instead of 1:1.
   *
   *  Once applied it never re-applies, so a track swap can't re-force a sync the user turned off, and
   *  a deck someone already enabled by hand is left exactly as it is. */
  applyDefaultSync() {
    if (this.#defaultSyncApplied) return;
    if (syncCoordinator?.isInitialized !== true) return;
    if (this.#nativeTempo == null) return;
    // In a ROOM the room decides: it enables every voice at Start, and a holder's deliberate
    // sync-off arrives in the state snapshot. A boot default here would be published as if the user
    // had chosen it (or silently dropped by the owner gate) and could overwrite what the room just
    // told us. Left unconsumed, so a deck outliving the room still gets its default later.
    if (this.#roomSync) return;
    this.#defaultSyncApplied = true;
    this.setSyncEnabled(true);
  }

  /** Toggle this deck's sync. First enabler in an in-tab realm ESTABLISHES the master from this
   *  deck's tempo (a room instead adopts a peer's tempo on join); a later enabler ADOPTS the running
   *  master via the coordinator's adopt-on-sync re-emit. Disabling HOLDS the current tempo — the
   *  deck keeps playing at the tempo it was synced to (no revert, no jump).
   *
   *  In a room this is SHARED state, routed exactly like warp: a slot holder's toggle applies here
   *  AND publishes, a non-holder's input is dropped before it touches the clock, and a remote apply
   *  passes `{ roomRemote: true }` so it neither gates nor echoes. Every client must render the same
   *  voice the same way — otherwise one side waits for the shared bar while the other starts free.
   *  @param {boolean} enabled
   *  @param {{ roomRemote?: boolean }} [options] */
  setSyncEnabled(enabled, { roomRemote = false } = {}) {
    // Room gate: a toggle on a deck this client may not edit never reaches the clock.
    if (this.#roomSync && !roomRemote && this.#roomSync.canControl?.() === false) return;
    const on = Boolean(enabled);
    if (on === this.#syncEnabled) return;
    // Capture BEFORE the aggregate flips: enabling fans the adopt-on-sync master synchronously and
    // this deck (synced by then) adopts it — reading after would establish the master with itself.
    const establishing = on
      && syncCoordinator.isRoom !== true
      && !voiceRegistry.all().some((v) => v?.deck !== this && v?.deck?.syncEnabled === true);
    const establishTempo = establishing ? this.#tempo : null;
    this.#syncEnabled = on;
    recomputeSyncAggregate();
    if (establishing && Number.isFinite(establishTempo) && establishTempo > 0) {
      syncCoordinator.setTempo(establishTempo, { sourceType: 'manual', byVoiceId: this.#voiceId });
    }
    // Share the applied toggle with the room (never a remote echo, never a refused toggle).
    if (this.#roomSync && !roomRemote) this.#roomSync.publish?.(on);
  }

  /**
   * Install (or clear, with null) the room SYNC bridge. Same shape and same rules as the warp
   * bridge: a USER toggle is applied locally AND published, `canControl` gates whose input may drive
   * it, and a remote apply passes `{ roomRemote: true }`. Room mode only — null keeps every path
   * byte-identical.
   * @param {{ canControl?: () => boolean, publish?: (on: boolean) => void }|null} bridge
   */
  setRoomSyncBridge(bridge) {
    this.#roomSync = bridge && typeof bridge === 'object' ? bridge : null;
  }

  // ── warp ─────────────────────────────────────────────────────────────────

  /** Whether this deck's audio time-stretches to its transport tempo (independent of sync). */
  get warp() { return this.#warp; }

  /** Whether this deck's track has no tempo yet — warp is locked off until one is set. */
  get tempoMissing() { return this.#nativeTempo == null; }

  /** The user's SAVED warp choice for this track, or null when nobody has expressed one.
   *
   *  Read this, never `warp`, when deciding what to persist. `warp` is the live flag and moves for
   *  reasons that are not preferences: the mobile speed lock forces it off, a missing tempo locks it
   *  off, and in a room a PEER's toggle moves it. Persisting the live value would write any of those
   *  down as this user's choice — a speed-locked phone would save "warp off" and win on desktop, and
   *  a room peer's toggle would become your setting. `setWarp` only updates this for a local toggle. */
  get warpPreference() { return this.#warpPreference; }

  setWarp(enabled, { roomRemote = false } = {}) {
    // Room gate: a user toggle on a deck this client may not edit is dropped before it touches the
    // rate owners (the same rule the loop bridge applies). Remote applies pass — they ARE the event.
    if (this.#roomWarp && !roomRemote && this.#roomWarp.canControl?.() === false) return;
    const on = enabled !== false;
    // Can't warp while the speed lock is engaged — the stretch it would ask for is blocked anyway.
    if (this.#speedLocked && on) return;
    // Can't warp without a tempo — there is no grid reference to stretch to.
    if (this.#nativeTempo == null && on) return;
    if (on === this.#warp) return;
    this.#warp = on;
    // A toggle the USER made is this track's preference from now on, so anything that later re-applies
    // the default (a tempo arriving, a reload within this session) honours the choice instead of
    // reverting to "on". A remote peer's toggle in a room is deliberately excluded: it moves this
    // deck because the room says so, but it is not this user's preference and is never persisted.
    if (!roomRemote) this.#warpPreference = on;
    // Re-fan status so both rate owners re-apply immediately (rate-only, no position seek).
    syncCoordinator.notifyVoiceWrapChanged?.();
    this.#emit('sync-status');
    // Share the applied toggle with the room (never a remote echo, never a refused toggle).
    if (this.#roomWarp && !roomRemote) this.#roomWarp.publish?.(on);
  }

  /**
   * Install (or clear, with null) the room WARP bridge. When present, a USER warp toggle becomes a
   * SHARED event: applied locally AND published; `canControl` gates whose input may drive it. A remote
   * apply passes `{ roomRemote: true }` to `setWarp` so it applies without gating or re-publishing.
   * Room mode only — null keeps every path byte-identical.
   * @param {{ canControl?: () => boolean, publish?: (on: boolean) => void }|null} bridge
   */
  setRoomWarpBridge(bridge) {
    this.#roomWarp = bridge && typeof bridge === 'object' ? bridge : null;
  }

  /**
   * Install (or clear, with null) the room GRID bridge — the shared-projection inputs this deck owns
   * (native tempo, downbeat, launch grid). See `#roomGrid`. Room mode only; null keeps every path
   * byte-identical.
   * @param {{ canControl?: () => boolean,
   *           publishNativeTempo?: (bpm: number) => void,
   *           publishDownbeat?: (sec: number) => void,
   *           publishLaunchGrid?: (bars: number) => void }|null} bridge
   */
  setRoomGridBridge(bridge) {
    this.#roomGrid = bridge && typeof bridge === 'object' ? bridge : null;
  }

  /** Does the room bridge refuse a USER write of a shared grid value on this deck? The three setters
   *  below return `false` on exactly this — and `true` otherwise, no-ops and clamps included — so a
   *  caller with follow-up work (persisting the value, re-aligning the playhead) can tell "the room
   *  said no" apart from "nothing to do", and never saves an edit the app declined to apply. */
  #roomGridRefuses(roomRemote) {
    return Boolean(this.#roomGrid) && !roomRemote && this.#roomGrid.canControl?.() === false;
  }

  /** Whether the mobile speed lock is engaged for this deck (audio pinned to native rate). */
  get speedLocked() { return this.#speedLocked; }

  /** Engaged/released by the audio engine when the mobile speed lock resolves. Engaging forces warp
   *  OFF (its stretch is a no-op under the lock, and this keeps the metronome/grid on the deck's real
   *  native tempo instead of a transport tempo the audio isn't playing). Always re-fans status so the
   *  Warp control can disable/re-enable itself. */
  setSpeedLocked(locked) {
    const on = locked === true;
    if (on === this.#speedLocked) return;
    this.#speedLocked = on;
    if (on && this.#warp) {
      this.#warp = false;
      syncCoordinator.notifyVoiceWrapChanged?.();
    }
    this.#emit('sync-status');
  }

  // ── tempo ────────────────────────────────────────────────────────────────

  /** The transport tempo — what the header number shows, and what warp stretches to. */
  get tempo() { return this.#tempo; }

  /** A user edit of the deck's tempo (the header number, by any input method). Synced (or solo):
   *  proposes the shared master — the coordinator's single gate decides. Unsynced collection deck:
   *  the deck's own transport tempo, session-only (deliberately NOT persisted — the track's saved
   *  tempo is `nativeTempo`; persisting transport tempos is how sibling tempos poisoned tracks). */
  setTempo(bpm, { sourceType = 'manual' } = {}) {
    // The transport's range is enforced HERE, on the one owner, so the tempo a deck holds is always
    // a tempo the readout can show. It used to be enforced only on the `sync-bpm` display param,
    // which left the deck free to run at 3 BPM behind a readout pinned at its floor.
    const next = clampTempo(toPositiveNumber(bpm));
    if (next == null) return;
    if (this.#syncEnabled || !this.#collection) {
      syncCoordinator.setTempo(next, { sourceType, byVoiceId: this.#voiceId });
      return;
    }
    if (next === this.#tempo) return;
    this.#tempo = next;
    this.#tempoIsPlaceholder = false;
    this.#emit('bpm');
  }

  /** This deck's OWN native track tempo (its grid reference — never the shared singleton). */
  get nativeTempo() { return this.#nativeTempo; }

  /** Set the native track tempo (a per-track edit from the kit panel; the persisted value). While
   *  the deck is on its own transport (unsynced collection), the transport follows the new native
   *  1:1 (ratio stays 1) unless the user has detached it by editing the number.
   *
   *  In a room this is SHARED state, routed like warp and sync: it is the DIVISOR of the shared-phase
   *  projection, so two clients on different values separate without limit. A non-holder's edit is
   *  dropped before it moves the grid; a remote apply passes `{ roomRemote: true }`.
   *  @param {number} bpm
   *  @param {{ roomRemote?: boolean }} [options]
   *  @returns {boolean} false only when the room refused this write (see `#roomGridRefuses`). */
  setNativeTempo(bpm, { roomRemote = false } = {}) {
    if (this.#roomGridRefuses(roomRemote)) return false;
    const next = toPositiveNumber(bpm);
    if (next == null || next === this.#nativeTempo) return true;
    // Same "is the transport still riding its track?" test the track seed uses — a PLACEHOLDER
    // transport (a collection deck that booted with no tempo, holding the master default) counts as
    // riding native, or the arriving tempo would leave the deck stretching to a number nobody chose.
    const wasRidingNative = this.#tempo == null
      || this.#tempo === this.#nativeTempo
      || this.#tempoIsPlaceholder;
    const wasMissing = this.#nativeTempo == null;
    this.#nativeTempo = next;
    this.#projectionGeneration += 1; // the beat→position scale just changed
    if (wasRidingNative && this.#collection && !this.#syncEnabled) {
      this.#tempo = next;
      this.#tempoIsPlaceholder = false;
    }
    if (wasMissing) {
      this.#restoreWarpAfterTempo();
      // A track that had NO tempo boots unsynced (the default defers — there was nothing to
      // establish the master from). The tempo has now arrived by hand, so treat this exactly like a
      // seed: a solo deck's transport IS the master, so seed that first, then take the default.
      if (!this.#collection) syncCoordinator.seedTrackTempo?.(next);
      this.applyDefaultSync();
    }
    this.#emit('bpm');
    // Share the APPLIED value (never a remote echo, never a refused or no-op write).
    if (this.#roomGrid && !roomRemote) this.#roomGrid.publishNativeTempo?.(next);
    return true;
  }

  /** Apply this track's saved warp preference to the live flag on load.
   *
   *  The two locks still outrank it, and for the same reason they outrank a user toggle: a deck with
   *  no tempo has no grid to stretch to, and a speed-locked deck cannot stretch at all. Neither is a
   *  disagreement with the preference — the preference is simply waiting, and `#restoreWarpAfterTempo`
   *  (tempo arrives) or `setSpeedLocked(false)` (lock releases) hands it back. */
  #applySavedWarp() {
    const desired = this.#speedLocked || this.#nativeTempo == null ? false : this.#warpDefault();
    if (desired === this.#warp) return;
    this.#warp = desired;
    syncCoordinator.notifyVoiceWrapChanged?.();
  }

  /** A tempo just arrived on a deck that had none: lift the no-tempo lock back to the warp default —
   *  the track's saved preference when it has one, else on. The mobile speed lock still wins; it
   *  keeps warp off until it releases. */
  #restoreWarpAfterTempo() {
    if (this.#speedLocked || this.#warp) return;
    if (this.#warpDefault() === false) return; // saved preference says off — nothing to restore
    this.#warp = true;
    syncCoordinator.notifyVoiceWrapChanged?.();
  }

  /** Whether this deck currently time-stretches at all: warp on, and (for a solo deck) the sync
   *  session live — the ONE site of the follow rule. */
  get following() {
    return this.#warp && (this.#syncEnabled || (this.#collection
      ? true
      : syncCoordinator?.isEnabled === true));
  }

  /** The playback-rate projection: transport / native when following, else exactly 1 (natural). */
  get followRatio() {
    if (!this.following) return 1;
    const tempo = toPositiveNumber(this.#tempo);
    const native = toPositiveNumber(this.#nativeTempo);
    return tempo != null && native != null ? tempo / native : 1;
  }

  // ── meter / grid ─────────────────────────────────────────────────────────

  /** This deck's own per-track meter id — a cheap field read for the hot paths. */
  get meter() { return this.#meter; }

  setMeter(meter) {
    const next = normalizeMeterId(meter);
    if (next === this.#meter) return;
    this.#meter = next;
    this.#emit('meter');
  }

  /** This deck's OWN launch grid in bars (0 = none) — per-deck; SHARED per deck inside a room. */
  get launchGridBars() { return this.#launchGridBars; }

  /** Set this deck's launch grid in bars (0 = none). Routed in a room: the grid names the boundary a
   *  shared start, seek and drift correction all quantize to, so two clients on different grids land
   *  on different bars for the same event. `{ roomRemote: true }` applies without gating or echoing —
   *  used by remote applies AND by the writes the user did not make (the room's own 'none' override,
   *  its restore on teardown, the `?launchGrid=` boot seed).
   *  @param {number} bars
   *  @param {{ roomRemote?: boolean }} [options]
   *  @returns {boolean} false only when the room refused this write. */
  setLaunchGridBars(bars, { roomRemote = false } = {}) {
    if (this.#roomGridRefuses(roomRemote)) return false;
    const n = Number(bars);
    // A room cannot honour a FREE launch (0 = 'none'): a shared start, seek or correction has to name
    // a boundary every client agrees on. Refused at the WRITE, not filtered at the apply — a sender
    // that accepted 0 while every receiver dropped it would leave the holder launching free while the
    // peers kept a grid, which is the split this whole routing exists to close. The pickers already
    // stop offering 'none' in a room; this covers the paths that don't go through them (gang replay,
    // MIDI, the URL pin). `roomRemote` still passes, so the room's teardown can hand 'none' back.
    if (this.#roomGrid && !roomRemote && n === 0) return false;
    if (!Number.isFinite(n) || n < 0 || n === this.#launchGridBars) return true;
    this.#launchGridBars = n;
    this.#emit('launch-grid');
    if (this.#roomGrid && !roomRemote) this.#roomGrid.publishLaunchGrid?.(n);
    return true;
  }

  /** The launch grid expressed in quarter-note beats over this deck's OWN meter (0 = none). */
  get launchGridQuarterBeats() {
    return this.#launchGridBars > 0 ? this.#launchGridBars * sharedBeatsPerBar(this.#meter) : 0;
  }

  getGridMarker() { return buildGridMarker(this.#gridMarkerTimeSec); }
  getGridMarkers() { return Object.freeze([this.getGridMarker()]); }
  getGridMarkerTimeSec() { return this.#gridMarkerTimeSec; }

  /** Move the downbeat (grid marker, source seconds). Routed in a room: it is the ORIGIN of the
   *  un-anchored projection, so two clients on different markers sit a constant offset apart from the
   *  first note. `{ roomRemote: true }` applies without gating or echoing.
   *  @param {number} gridMarkerTimeSec
   *  @param {{ roomRemote?: boolean }} [options]
   *  @returns {boolean} false only when the room refused this write. */
  setGridMarkerTimeSec(gridMarkerTimeSec, { roomRemote = false } = {}) {
    if (this.#roomGridRefuses(roomRemote)) return false;
    const next = clampGridStartTimeSec(gridMarkerTimeSec);
    if (Math.abs(next - this.#gridMarkerTimeSec) <= 0.0005) return true;
    this.#gridMarkerTimeSec = next;
    this.#projectionGeneration += 1; // the un-anchored projection reads the marker directly
    this.#emit('grid-marker');
    // Publish the CLAMPED value, not the raw request — every client must fold to the same number.
    if (this.#roomGrid && !roomRemote) this.#roomGrid.publishDownbeat?.(next);
    return true;
  }

  // ── clock seam ───────────────────────────────────────────────────────────

  /** Inject the shared-clock snapshot source (the realm-wide shared clock; a function so the beat
   *  read stays live — never cache the beat). */
  setSharedClockSource(fn) {
    this.#sharedClockSource = typeof fn === 'function' ? fn : null;
  }

  /** Inject this deck's own playback-position source: () => ({ playing, positionMs }). Wired by the
   *  audio adapter once playback exists. */
  setPositionSource(fn) {
    this.#positionSource = typeof fn === 'function' ? fn : null;
  }

  /** The raw shared-clock snapshot (or null when no source / not joined) — for consumers that need
   *  the full joined-state payload (quantized launch), not just a beat. */
  getSharedClockState() {
    if (!this.#sharedClockSource) return null;
    try { return this.#sharedClockSource(); } catch (_) { return null; }
  }

  /**
   * The deck's beat clock — the choice every per-player feature used to hand-assemble, behind ONE
   * seam. Returns `{ beatNow, secondsPerBeat }` or null when there is nothing to click to:
   *  - synced with a live shared session → the SHARED clock (same beat reference as launch
   *    quantization, room-wide);
   *  - otherwise → this deck's OWN playback: beat position from its source position relative to ITS
   *    grid marker (beats live on the source grid, so this is rate-invariant), wall tempo = the
   *    transport tempo it is actually playing at (native × followRatio).
   */
  clock() {
    if (this.#syncEnabled) {
      const shared = this.getSharedClockState();
      if (shared && shared.joined === true) {
        const sharedBpm = Number(shared.tempoBpm);
        const beatNow = Number(shared.beatNow);
        if (sharedBpm > 0 && Number.isFinite(beatNow)) {
          return { beatNow, secondsPerBeat: 60 / sharedBpm };
        }
      }
    }
    const pos = this.#positionSource ? this.#positionSource() : null;
    if (!pos || pos.playing !== true) return null;
    const native = toPositiveNumber(this.#nativeTempo);
    const posSec = Number(pos.positionMs) / 1000;
    if (native == null || !Number.isFinite(posSec)) return null;
    const wallBpm = native * this.followRatio;
    return {
      beatNow: (posSec - this.#gridMarkerTimeSec) * (native / 60),
      secondsPerBeat: 60 / wallBpm,
    };
  }

  // ── alignment math (one owner of beat→playhead mapping) ──────────────────

  /** The PHASE ANCHOR this deck currently projects from, or null while it rides the grid marker.
   *  `beat` is an absolute SHARED-clock beat; `sourceMs` is a RAW source position (no device lead —
   *  the lead is this device's own and is added on projection). */
  get seekAnchor() {
    return this.#anchorBeat == null
      ? null
      : { beat: this.#anchorBeat, sourceMs: this.#anchorSourceMs };
  }

  /** A generation token for the projection inputs — see `#projectionGeneration`. */
  get projectionGeneration() { return this.#projectionGeneration; }

  /**
   * REPLACE the phase anchor: "at shared beat `beat`, this voice is at raw source `sourceMs`".
   *
   * A seek replaces the anchor; a start aligns THROUGH it. It is deliberately NOT cleared by the
   * forced alignment at start — a late joiner is handed the room's phase and then starts, so
   * resetting it there would wipe the very thing it was just told and diverge by construction. Only
   * a new track clears it (`seedFromTrackData`), because the grid it described is gone.
   * @param {number} beat absolute shared-clock beat.
   * @param {number} sourceMs raw (un-led) source position at that beat.
   */
  setSeekAnchor(beat, sourceMs) {
    // `?? NaN` before the coercion: `Number(null)` is 0, so a half-set anchor would otherwise be
    // accepted as a real "beat 0" reference and project every later read from the wrong place.
    const anchorBeat = Number(beat ?? NaN);
    const anchorSourceMs = Number(sourceMs ?? NaN);
    if (!Number.isFinite(anchorBeat) || !Number.isFinite(anchorSourceMs)) return;
    if (anchorBeat === this.#anchorBeat && anchorSourceMs === this.#anchorSourceMs) return;
    this.#anchorBeat = anchorBeat;
    this.#anchorSourceMs = anchorSourceMs;
    this.#projectionGeneration += 1;
  }

  /** Drop the anchor back to the grid-marker default (a new track — the old phase means nothing). */
  clearSeekAnchor() {
    if (this.#anchorBeat == null) return;
    this.#anchorBeat = null;
    this.#anchorSourceMs = null;
    this.#projectionGeneration += 1;
  }

  /** The shared beat, as ONE read: the deck's own shared-clock source while joined, else the
   *  coordinator's free-running count (which is what an unjoined deck has). Every projection and
   *  every scheduler in the audio path goes through here — never a second beat source. */
  getCurrentSyncBeat() {
    const shared = this.getSharedClockState();
    if (shared && shared.joined === true && Number.isFinite(shared.beatNow)) {
      return shared.beatNow + 1;
    }
    if (typeof syncCoordinator?.getCurrentBeat === 'function') {
      return syncCoordinator.getCurrentBeat() + 1;
    }
    return 1;
  }

  /** The source position the anchor implies at the shared beat, BEFORE any wrap:
   *  `anchorSourceMs + (beatNow − anchorBeat) · msPerBeat(native)`. Un-wrapped it grows without
   *  bound, which is exactly what `phaseLoopRange` needs to ask "has the phase reached the loop yet". */
  #rawProjectedSourceMs() {
    const native = toPositiveNumber(this.#nativeTempo);
    if (native == null) return null;
    const beatNow = this.getCurrentSyncBeat() - 1;
    if (!Number.isFinite(beatNow)) return null;
    const anchorSourceMs = this.#anchorOrMarkerSourceMs();
    if (!Number.isFinite(anchorSourceMs)) return null;
    return anchorSourceMs + ((beatNow - (this.#anchorBeat ?? 0)) * (60_000 / native));
  }

  /** The anchor's source position, or the grid-marker default that makes an un-sought voice
   *  byte-identical to the historical grid-marker formula. */
  #anchorOrMarkerSourceMs() {
    return this.#anchorBeat == null ? this.#gridMarkerTimeSec * 1000 : this.#anchorSourceMs;
  }

  /**
   * Which regime the SHARED PHASE is expressed in — the loop, or the whole track.
   *
   * Resolved from the ANCHOR and the loop alone, NEVER from a playhead. Two clients holding the same
   * anchor and the same loop have to project the same position, and the playhead is the one input
   * that is legitimately different on each of them: reading it here let two clients on opposite sides
   * of a loop edge fold the same shared phase through different periods and converge, each perfectly
   * self-consistently, a whole loop-length apart.
   *
   * The loop owns the phase once the projection has REACHED it. That is what lets a loop drawn ahead
   * of the playhead wait — folding the phase into a loop it has not travelled to yet would teleport
   * it forward — while a phase already inside keeps looping with it.
   *
   * An anchor PAST the loop's end is the room deliberately LEAVING the loop: a seek out replaces the
   * anchor with the point it left for, and that phase belongs to the track. Without this the
   * projection would wrap straight back inside, which is the seek reading as ignored. Strictly past,
   * because the loop is the half-open `[start, end)` the wrap implies: an anchor exactly ON `end` has
   * already wrapped to `start`, and the sink engages there too — calling it "outside" would put the
   * phase on the track while the playhead wrapped into the loop, which is the divergence this rule
   * exists to remove.
   *
   * @param {{start:number,end:number}|null} loopRange the loop this voice logically has (enabled),
   *   which is shared state — not whichever loop happens to contain the local playhead.
   * @returns {{start:number,end:number}|null} the loop to fold the phase through, or null for the track.
   */
  phaseLoopRange(loopRange = null) {
    const start = Number(loopRange?.start);
    const end = Number(loopRange?.end);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;
    const anchorSourceMs = this.#anchorOrMarkerSourceMs();
    if (!Number.isFinite(anchorSourceMs) || anchorSourceMs > end) return null;
    const raw = this.#rawProjectedSourceMs();
    if (raw == null || raw < start) return null;
    return loopRange;
  }

  /**
   * The source position the shared beat implies RIGHT NOW — the one owner of beat→playhead mapping.
   *
   *   expected = anchorSourceMs + (beatNow − anchorBeat) · msPerBeat(native)
   *
   * wrapped into the loop it is given (`phaseLoopRange` is what decides that, and every shared-phase
   * caller resolves it through there), then led by this device's manual audio offset. With no anchor
   * set the default `(beat 0, grid marker)` makes this exactly the historical grid-marker formula, so
   * an un-sought voice is byte-identical.
   */
  computeAlignedSourcePositionMs({ durationMs = 0, loopRange = null, outputLeadMs = 0 } = {}) {
    const sourceMs = this.#rawProjectedSourceMs();
    if (sourceMs == null) return null;
    const alignedMs = wrapSourcePositionMs({ sourceTimeSec: sourceMs / 1000, durationMs, loopRange });
    return this.leadSourcePositionMs(alignedMs, { durationMs, loopRange, outputLeadMs });
  }

  /** Snap a source position (ms) to the NEAREST boundary of this deck's OWN launch grid — bar
   *  lines of `launchGridQuarterBeats` quarter-beats at the native tempo, anchored on the grid
   *  marker. Used by an unsynced launch: Play comes in on the deck's own bar (there is no clock to
   *  wait on while stopped — the deck's beat IS its position, so the snap moves the playhead, not
   *  the start time). Returns null when there is nothing to snap to (not following / no grid / no
   *  native tempo), so the caller starts untouched.
   *  @param {number} positionMs
   *  @param {{ launchGridQuarterBeats?: number, durationMs?: number, loopRange?: {start:number,end:number}|null }} [opts]
   *  @returns {number|null} */
  snapToOwnGridMs(positionMs, { launchGridQuarterBeats = 0, durationMs = 0, loopRange = null } = {}) {
    if (!this.following) return null; // warp off = no grid
    const quarterBeats = Number(launchGridQuarterBeats);
    const native = toPositiveNumber(this.#nativeTempo);
    if (!(quarterBeats > 0) || native == null) return null;
    const posSec = Number(positionMs) / 1000;
    if (!Number.isFinite(posSec)) return null;
    const barSec = quarterBeats * (60 / native);
    let snappedSec = this.#gridMarkerTimeSec
      + Math.round((posSec - this.#gridMarkerTimeSec) / barSec) * barSec;
    // The nearest boundary can land before the track start — take the first one at/after 0 instead.
    if (snappedSec < 0) snappedSec += Math.ceil(-snappedSec / barSec) * barSec;
    return wrapSourcePositionMs({ sourceTimeSec: snappedSec, durationMs, loopRange });
  }

  /** Manual audio offset — advance a source position (ms) by `outputLeadMs` of WALL time so this
   *  device's audio leaves the speaker that many ms earlier. Wall-ms → source-ms scales by the
   *  follow ratio; a zero lead is a NO-OP (the position is returned untouched, not even re-wrapped),
   *  so plain seeks stay byte-identical when the offset is off. */
  leadSourcePositionMs(sourceMs, { durationMs = 0, loopRange = null, outputLeadMs = 0 } = {}) {
    const base = Number(sourceMs);
    if (!outputLeadMs || !Number.isFinite(base)) return base;
    const rate = Number(this.followRatio) > 0 ? Number(this.followRatio) : 1;
    const ledMs = base + outputLeadMs * rate;
    // A NEGATIVE offset (this device's audio has to leave LATER) carries the position backwards, and
    // the wrap below is modular — so a position at or near the track start comes back round at the
    // END. Outside a loop that is never what anyone meant: there is nothing before the start to
    // compensate INTO, and the answer "the last 56 ms of the track" plays for an instant and stops.
    // The start is the honest answer and the rest of the compensation is simply spent. Inside a loop
    // the wrap keeps its meaning — the playhead really is cycling that range — and is left alone.
    //
    // This sits HERE, at the one owner of the lead, because both readers need it: the seek path
    // (`_leadSeekPositionMs`) and the beat→playhead alignment (`computeAlignedSourcePositionMs`),
    // which re-applies the lead at every start and would otherwise undo a rewind the seek got right.
    //
    // KNOWN COST, accepted deliberately: on the alignment path a position in `0…|lead·rate|` can
    // also mean "the shared phase has just wrapped past the end", where leading back into the tail
    // of the previous pass was right — so a synced, non-looping deck loses those last few ms on each
    // wrap. Telling the two apart needs the CALLER's intent, not anything readable from here, and
    // scoping the clamp back to seeks alone reopens the original bug: a restart anchors at zero and
    // the align that follows re-applies the lead to ≈0. Do not "fix" this by narrowing it to the
    // seek path without first threading that intent down from both callers.
    if (!loopRange && ledMs < 0) return 0;
    return wrapSourcePositionMs({ sourceTimeSec: ledMs / 1000, durationMs, loopRange });
  }

  // ── coordinator feeds ────────────────────────────────────────────────────

  /** The coordinator fans every master tempo change to EVERY deck; the adopt gate lives HERE (the
   *  one gate): a deck adopts the master into its transport tempo only while it follows the master
   *  (synced, or solo). An unsynced collection deck ignores it — its tempo is its own. */
  receiveMasterChange(detail) {
    if (this.#disposed) return;
    const master = toPositiveNumber(detail?.bpm);
    if (master == null) return;
    if (!(this.#syncEnabled || !this.#collection)) return;
    if (master === this.#tempo) return;
    this.#tempo = master;
    this.#tempoIsPlaceholder = false;
    this.#emit('bpm');
  }

  /** Session/status change from the coordinator (enable/disable/mode/peers). The deck merges its
   *  OWN flags into the detail it retains (late subscribers read this deck's truth), but re-emits
   *  deck-changed only when its own enable/warp state actually moved — peer/mode churn is chrome's
   *  business (the coordinator's session channel), not the audio path's. */
  receiveStatusChange(detail) {
    if (this.#disposed) return;
    const previous = this.#lastStatusDetail;
    this.#lastStatusDetail = detail == null ? null : {
      ...detail,
      enabled: this.#collection ? this.#syncEnabled : detail?.enabled === true,
      wrapEnabled: this.#warp,
    };
    const changed =
      previous?.enabled !== this.#lastStatusDetail?.enabled ||
      previous?.wrapEnabled !== this.#lastStatusDetail?.wrapEnabled;
    if (changed) this.#emit('sync-status');
  }

  /** Last status detail this deck retained (null until the first feed) — late-subscriber truth. */
  getStatusDetail() { return this.#lastStatusDetail; }

  // ── notification ─────────────────────────────────────────────────────────

  /** Subscribe to deck-changed. Listener gets (snapshot, reason); reasons: 'bpm' | 'sync-status' |
   *  'meter' | 'grid-marker'. Returns an unsubscribe fn. */
  onChange(listener) {
    if (typeof listener !== 'function') return () => {};
    this.#listeners.add(listener);
    return () => { this.#listeners.delete(listener); };
  }

  /** The deck's public snapshot. Keys `bpm`/`trackBpm`/`baseRate`/`syncEnabled`/`wrapEnabled` are
   *  the legacy wire names (the `orbiters:wrap-grid-change` payload React stores/tests already
   *  read); the class getters (`tempo`/`nativeTempo`/`followRatio`/`warp`) are the canonical API. */
  getSnapshot() {
    const gridMarker = buildGridMarker(this.#gridMarkerTimeSec);
    return {
      voiceId: this.#voiceId,
      following: this.following,
      syncEnabled: this.#collection ? this.#syncEnabled : syncCoordinator?.isEnabled === true,
      wrapEnabled: this.#warp,
      launchGridBars: this.#launchGridBars,
      gridMarkerTimeSec: this.#gridMarkerTimeSec,
      gridMarker,
      gridMarkers: Object.freeze([gridMarker]),
      gridStartTimeSec: this.#gridMarkerTimeSec,
      bpm: this.#tempo,
      trackBpm: this.#nativeTempo,
      tempoMissing: this.#nativeTempo == null,
      baseRate: this.followRatio,
      meter: this.#meter,
    };
  }

  #emit(reason) {
    if (this.#disposed) return;
    const snapshot = this.getSnapshot();
    this.#listeners.forEach((listener) => {
      try {
        listener(snapshot, reason);
      } catch (error) {
        console.error('[Deck] listener error', error);
      }
    });
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(WRAP_GRID_CHANGE_EVENT, {
        detail: { ...snapshot, reason },
      }));
    }
  }

  /** Voice teardown: release sync membership (so removing the only synced deck releases the
   *  session), then go inert. */
  dispose() {
    if (this.#disposed) return;
    const wasSynced = this.#syncEnabled;
    this.#syncEnabled = false;
    this.#disposed = true;
    this.#listeners.clear();
    this.#lastStatusDetail = null;
    this.#sharedClockSource = null;
    this.#positionSource = null;
    if (wasSynced) recomputeSyncAggregate();
  }
}
